/**
 * ServersV2.jsx — AudioSocket server management
 *
 * Layout: left sidebar (server list) + main panel with 2 tabs (Info, Bots)
 * Reuses ServersV2.css from the prior Linux page for theme consistency.
 *
 * Features:
 *   - List / add / edit / delete audiosocket_servers
 *   - Install Asterisk Stack as a background job, with live progress + log tail
 *   - CPU / RAM / Disk gauges in the Info tab (SSH-probed every 30s)
 *   - Bots tab: AudioSocket bot LXCs filtered to this server, start/stop/restart/delete + expand for log/recordings
 *   - "Clone Bots" — pick any AudioSocket template, count, base LXC id; live progress via job polling
 */

import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Server, Cpu, MemoryStick, HardDrive, RefreshCw, Plus, Edit2, Trash2,
    Download, Play, Square, RotateCw, ChevronRight, ChevronDown, X, Save,
    CheckCircle2, AlertCircle, Loader2, Search, Wifi, FileText, Zap,
    ArrowLeft, PauseCircle, CircleSlash, Boxes, FileCog
} from 'lucide-react'
import { audiosocketBotsApi } from '../services/api'
import StatCards from '../components/StatCards'
import { NewHetznerServerModal, BootstrapProgressModal } from '../components/BootstrapServer'
import BotEnvModal from '../components/BotEnvModal'
import './ServersV2.css'

// Same rule as the backend's MACHINE_STOPPED_STATUSES.
export const CONTAINER_STOPPED_STATUSES = ['offline', 'deactivated', 'removed']
export const isContainerStopped = (m) => CONTAINER_STOPPED_STATUSES.includes(m.status)

// running = at least one running container · stopped = has containers, none
// running · unused = no containers at all.
function serverUsage(machines) {
    if (machines.length === 0) return 'unused'
    return machines.some(m => !isContainerStopped(m)) ? 'running' : 'stopped'
}

export default function ServersV2() {
    // /servers → overview of every server; /servers/:id → that server's LXCs + info
    const { id: routeId } = useParams()
    const navigate = useNavigate()
    const selectedId = routeId ? parseInt(routeId, 10) : null
    const [servers, setServers] = useState([])
    const [machines, setMachines] = useState([])
    const [loading, setLoading] = useState(true)
    const [tab, setTab] = useState('bots')
    const [search, setSearch] = useState('')
    const [usageFilter, setUsageFilter] = useState('')

    const [editingServer, setEditingServer] = useState(null)
    const [installingServer, setInstallingServer] = useState(null)  // {server, force?}
    const [templatingServer, setTemplatingServer] = useState(null)  // {server, force?}
    const [cloningOnServer, setCloningOnServer] = useState(null)
    const [installLogModal, setInstallLogModal] = useState(null)
    const [newHetzner, setNewHetzner] = useState(false)
    const [bootstrapId, setBootstrapId] = useState(null)   // server id whose setup progress is open

    async function loadServers() {
        try {
            const r = await audiosocketBotsApi.listServers()
            setServers(r.data.servers || [])
        } catch (e) { console.error(e) }
    }
    async function loadMachines() {
        try {
            const r = await audiosocketBotsApi.listMachines()
            setMachines(r.data.machines || [])
        } catch (e) { console.error(e) }
    }
    async function loadAll() {
        setLoading(true)
        await Promise.all([loadServers(), loadMachines()])
        setLoading(false)
    }

    useEffect(() => { loadAll() }, [])

    const selectedServer = useMemo(
        () => servers.find(s => s.id === selectedId),
        [servers, selectedId]
    )
    const serverMachines = useMemo(
        () => machines.filter(m => m.server_id === selectedId),
        [machines, selectedId]
    )
    const machinesByServer = useMemo(() => {
        const m = {}
        for (const x of machines) (m[x.server_id] = m[x.server_id] || []).push(x)
        return m
    }, [machines])
    const usageOf = (s) => serverUsage(machinesByServer[s.id] || [])
    const usageCounts = useMemo(() => {
        const c = { running: 0, stopped: 0, unused: 0 }
        for (const s of servers) c[serverUsage(machinesByServer[s.id] || [])]++
        return c
    }, [servers, machinesByServer])
    const filteredServers = useMemo(() => {
        const q = search.toLowerCase()
        return servers.filter(s =>
            (!usageFilter || serverUsage(machinesByServer[s.id] || []) === usageFilter) &&
            (!q || (s.name || '').toLowerCase().includes(q) || (s.host_ip || '').toLowerCase().includes(q))
        )
    }, [servers, search, usageFilter, machinesByServer])

    async function deleteServer(s) {
        if (!window.confirm(`Delete server "${s.name}"?\n\nIts Asterisk LXC (#${s.asterisk_lxc_id || '?'}) and bot LXCs are NOT destroyed automatically — remove them on the host first if you don't want them.`)) return
        try {
            await audiosocketBotsApi.deleteServer(s.id)
            if (selectedId === s.id) navigate('/servers')
            await loadAll()
        } catch (e) {
            alert(e.response?.data?.error || e.message)
        }
    }

    const runningContainers = machines.filter(m => !isContainerStopped(m)).length
    const toggleUsage = (k) => setUsageFilter(f => f === k ? '' : k)

    return (
        <div className="servers-page-v2">
            {!selectedId ? (
                /* ============ OVERVIEW: all servers ============ */
                <div className="servers-overview">
                    <div className="overview-header">
                        <div>
                            <h1 className="overview-title"><Server size={22} /> Servers</h1>
                            <div className="overview-subtitle">Proxmox hosts running AudioSocket bot LXCs. Click a server to see its containers.</div>
                        </div>
                        <div className="overview-actions">
                            <button className="btn btn-icon" onClick={loadAll} title="Refresh">
                                <RefreshCw size={14} className={loading ? 'spinning' : ''} />
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditingServer({})}
                                title="Register a Proxmox host that is already set up">
                                <Plus size={14} /> Add existing
                            </button>
                            <button className="btn btn-primary btn-sm" onClick={() => setNewHetzner(true)}
                                title="Install + set up a fresh Hetzner server from its rescue system">
                                <Zap size={14} /> New Hetzner server
                            </button>
                        </div>
                    </div>

                    <StatCards cards={[
                        { key: 'all', label: 'All servers', value: servers.length, icon: Server, color: '#60a5fa',
                          sub: `${machines.length} containers total`, onClick: () => setUsageFilter(''), active: !usageFilter },
                        { key: 'running', label: 'Running', value: usageCounts.running, icon: Zap, color: '#22c55e',
                          sub: `${runningContainers} containers running`, onClick: () => toggleUsage('running'), active: usageFilter === 'running',
                          title: 'Servers with at least one running container' },
                        { key: 'stopped', label: 'Stopped', value: usageCounts.stopped, icon: PauseCircle, color: '#f59e0b',
                          sub: 'containers, none running', onClick: () => toggleUsage('stopped'), active: usageFilter === 'stopped',
                          title: 'Servers that have containers, but none of them running' },
                        { key: 'unused', label: 'Unused', value: usageCounts.unused, icon: CircleSlash, color: '#94a3b8',
                          sub: 'no containers', onClick: () => toggleUsage('unused'), active: usageFilter === 'unused',
                          title: 'Servers with no bot containers' }
                    ]} />

                    <div className="overview-search">
                        <Search size={14} />
                        <input type="text" placeholder="Filter servers by name or IP..."
                            value={search} onChange={e => setSearch(e.target.value)} />
                    </div>

                    {loading && servers.length === 0 ? (
                        <div className="overview-empty"><Loader2 size={20} className="spinning" /></div>
                    ) : filteredServers.length === 0 ? (
                        <div className="overview-empty">
                            {servers.length === 0 ? 'No servers yet. Click Add Server.' : 'No servers match.'}
                        </div>
                    ) : (
                        <div className="server-grid">
                            {filteredServers.map(s => {
                                const list = machinesByServer[s.id] || []
                                const running = list.filter(m => !isContainerStopped(m)).length
                                const attention = list.filter(m => m.health?.alert).length
                                const usage = usageOf(s)
                                const lightClass = s.asterisk_status === 'installed' ? 'green' :
                                                   s.asterisk_status === 'installing' ? 'yellow' : 'red'
                                return (
                                    <div key={s.id} className="server-card" onClick={() => navigate(`/servers/${s.id}`)}>
                                        <div className="server-card-head">
                                            <span className={`status-light ${lightClass}`}
                                                title={`Asterisk: ${s.asterisk_status || 'not installed'}`}></span>
                                            <div className="server-card-name">{s.name}</div>
                                            {s.bootstrap_status && s.bootstrap_status !== 'done'
                                                ? <span className={`usage-pill ${s.bootstrap_status === 'running' ? 'running' : 'stopped'}`}>
                                                    {s.bootstrap_status === 'running' ? 'setting up' : 'setup needs help'}
                                                  </span>
                                                : <span className={`usage-pill ${usage}`}>{usage}</span>}
                                        </div>
                                        <div className="server-card-ip">{s.host_ip}</div>
                                        <div className="server-card-counts">
                                            <span><Boxes size={13} /> {list.length} container{list.length === 1 ? '' : 's'}</span>
                                            <span className="running">{running} running</span>
                                            {list.length - running > 0 && <span className="stopped">{list.length - running} stopped</span>}
                                            {attention > 0 && <span className="attention">{attention} need attention</span>}
                                        </div>
                                        <div className="server-card-meta">
                                            <span>{s.asterisk_status === 'installed' ? `Asterisk LXC ${s.asterisk_lxc_id}` : `Asterisk ${s.asterisk_status || 'not installed'}`}</span>
                                            <span>Template {s.golden_template_status === 'ready' ? `#${s.golden_template_vmid}` : (s.golden_template_status || 'none')}</span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            ) : !selectedServer ? (
                <div className="servers-overview">
                    <button className="back-link" onClick={() => navigate('/servers')}><ArrowLeft size={14} /> All servers</button>
                    <div className="overview-empty">
                        {loading ? <Loader2 size={20} className="spinning" /> : `Server #${selectedId} not found.`}
                    </div>
                </div>
            ) : (
                /* ============ DETAIL: one server ============ */
                <main className="server-content server-detail">
                    <div className="content-header">
                        <div className="content-header-left">
                            <button className="back-link" onClick={() => navigate('/servers')} title="Back to all servers">
                                <ArrowLeft size={14} /> All servers
                            </button>
                            <span className="crumb-sep">/</span>
                            <h2>{selectedServer.name}</h2>
                            <span className="server-item-ip">{selectedServer.host_ip}</span>
                        </div>
                        <div className="content-header-right">
                            <button className="btn btn-icon" onClick={loadAll} title="Refresh">
                                <RefreshCw size={14} className={loading ? 'spinning' : ''} />
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditingServer(selectedServer)}>
                                <Edit2 size={14} /> Edit
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => deleteServer(selectedServer)}>
                                <Trash2 size={14} /> Delete
                            </button>
                        </div>
                    </div>

                    {selectedServer.bootstrap_status && selectedServer.bootstrap_status !== 'done' && (
                        <div className="tw-mx-5 tw-mt-4 tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-blue-500/40 tw-bg-blue-500/10 tw-px-4 tw-py-3">
                            {selectedServer.bootstrap_status === 'running'
                                ? <Loader2 size={18} className="spinning tw-text-blue-400" />
                                : <AlertCircle size={18} className="tw-text-amber-400" />}
                            <div className="tw-flex-1 tw-text-sm">
                                <div className="tw-font-semibold">
                                    {selectedServer.bootstrap_status === 'running' ? 'Server is being set up' : 'Server setup stopped — needs your help'}
                                </div>
                                <div className="tw-text-xs tw-text-muted">
                                    {selectedServer.bootstrap_phase ? `Last finished step: ${selectedServer.bootstrap_phase}` : 'Starting…'}
                                </div>
                            </div>
                            <button className="btn btn-primary btn-sm" onClick={() => setBootstrapId(selectedServer.id)}>
                                {selectedServer.bootstrap_status === 'running' ? 'Show progress' : 'Show + resume'}
                            </button>
                        </div>
                    )}

                    <div className="content-tabs">
                        <button
                            className={`content-tab ${tab === 'bots' ? 'active' : ''}`}
                            onClick={() => setTab('bots')}>
                            LXCs ({serverMachines.length})
                        </button>
                        <button
                            className={`content-tab ${tab === 'info' ? 'active' : ''}`}
                            onClick={() => setTab('info')}>
                            Info
                        </button>
                    </div>

                    {tab === 'info' && (
                        <InfoTab
                            server={selectedServer}
                            onInstallAsterisk={() => setInstallingServer({ server: selectedServer, force: false })}
                            onReinstall={() => {
                                if (window.confirm(`Reinstall Asterisk on "${selectedServer.name}"?\n\nThis will:\n  1. Stop and DESTROY existing LXC #${selectedServer.asterisk_lxc_id}\n  2. Create a fresh LXC + install Asterisk\n  3. Re-push current bot SIP/extensions config\n\nBot LXCs are NOT touched, but they'll be unable to connect until reinstall finishes (~3-5 min).`)) {
                                    setInstallingServer({ server: selectedServer, force: true })
                                }
                            }}
                            onShowLog={() => setInstallLogModal(selectedServer)}
                            onCreateTemplate={() => setTemplatingServer({ server: selectedServer, force: !!selectedServer.golden_template_vmid })}
                        />
                    )}
                    {tab === 'bots' && (
                        <BotsTab
                            server={selectedServer}
                            machines={serverMachines}
                            onReload={loadAll}
                            onCloneBots={() => setCloningOnServer(selectedServer)}
                        />
                    )}
                </main>
            )}

            {/* Modals */}
            {editingServer !== null && (
                <ServerForm
                    initial={editingServer}
                    onCancel={() => setEditingServer(null)}
                    onSaved={() => { setEditingServer(null); loadAll() }}
                />
            )}
            {installingServer && (
                <InstallAsteriskModal
                    server={installingServer.server}
                    force={installingServer.force}
                    onClose={() => { setInstallingServer(null); loadAll() }}
                />
            )}
            {templatingServer && (
                <CreateTemplateModal
                    server={templatingServer.server}
                    force={templatingServer.force}
                    onClose={() => { setTemplatingServer(null); loadAll() }}
                />
            )}
            {cloningOnServer && (
                <CloneBotsModal
                    server={cloningOnServer}
                    onClose={() => { setCloningOnServer(null); loadAll() }}
                />
            )}
            {newHetzner && (
                <NewHetznerServerModal
                    onClose={() => setNewHetzner(false)}
                    onStarted={(id) => { setNewHetzner(false); loadAll(); navigate(`/servers/${id}`); setBootstrapId(id) }}
                />
            )}
            {bootstrapId && (
                <BootstrapProgressModal serverId={bootstrapId} onClose={() => { setBootstrapId(null); loadAll() }} />
            )}
            {installLogModal && (
                <div className="modal-overlay" onClick={() => setInstallLogModal(null)}>
                    <div className="modal" style={{ maxWidth: 800 }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Asterisk install log — {installLogModal.name}</h3>
                            <button className="modal-close" onClick={() => setInstallLogModal(null)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <pre style={{
                                background: '#000', color: '#86efac', padding: 12, fontSize: 11,
                                borderRadius: 4, maxHeight: 500, overflow: 'auto', fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {installLogModal.asterisk_install_log || '(no log)'}
                            </pre>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

// ============================================================
// INFO TAB
// ============================================================

function InfoTab({ server, onInstallAsterisk, onShowLog, onReinstall, onCreateTemplate }) {
    const [stats, setStats] = useState(null)
    const [statsErr, setStatsErr] = useState(null)
    const [statsLoading, setStatsLoading] = useState(true)

    async function loadStats() {
        setStatsLoading(true)
        try {
            const r = await audiosocketBotsApi.serverStats(server.id)
            if (r.data.success) {
                setStats(r.data); setStatsErr(null)
            } else {
                setStatsErr(r.data.error || 'stats unavailable')
            }
        } catch (e) {
            setStatsErr(e.response?.data?.error || e.message)
        } finally { setStatsLoading(false) }
    }

    useEffect(() => {
        loadStats()
        const t = setInterval(loadStats, 30000)
        return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [server.id])

    const usageClass = (pct) => pct >= 90 ? 'critical' : pct >= 70 ? 'warning' : 'normal'

    return (
        <div className="server-info-tab">
            {/* Asterisk status banner */}
            {server.asterisk_status === 'installed' ? (
                <div className="info-banner" style={{ background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.3)' }}>
                    <CheckCircle2 size={20} style={{ color: '#22c55e' }} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>Asterisk installed</div>
                        <div style={{ fontSize: 12, opacity: 0.75 }}>LXC #{server.asterisk_lxc_id}</div>
                    </div>
                    {server.asterisk_install_log && (
                        <button className="btn btn-secondary btn-sm" onClick={onShowLog}>
                            <FileText size={14} /> View Log
                        </button>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={onReinstall}
                        title="Destroy the existing Asterisk LXC and reinstall a fresh one">
                        <RotateCw size={14} /> Reinstall
                    </button>
                </div>
            ) : server.asterisk_status === 'installing' ? (
                <div className="info-banner" style={{ background: 'rgba(251,191,36,0.1)', borderColor: 'rgba(251,191,36,0.3)' }}>
                    <Loader2 size={20} style={{ color: '#fbbf24' }} className="spinning" />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>Asterisk install in progress…</div>
                        <div style={{ fontSize: 12, opacity: 0.75 }}>Reopen the progress modal to see live status.</div>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={onInstallAsterisk}>
                        <Wifi size={14} /> Show progress
                    </button>
                </div>
            ) : (
                <div className="info-banner" style={{ background: 'rgba(220,38,38,0.1)', borderColor: 'rgba(220,38,38,0.3)' }}>
                    <AlertCircle size={20} style={{ color: '#fca5a5' }} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>Asterisk {server.asterisk_status || 'not installed'}</div>
                        <div style={{ fontSize: 12, opacity: 0.75 }}>Install the Asterisk stack before cloning bots on this server.</div>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={onInstallAsterisk}>
                        <Download size={14} /> Install Asterisk Stack
                    </button>
                    {server.asterisk_install_log && (
                        <button className="btn btn-secondary btn-sm" onClick={onShowLog}>
                            <FileText size={14} /> Last log
                        </button>
                    )}
                </div>
            )}

            {/* Golden bot template — only relevant once Asterisk is installed */}
            {server.asterisk_status === 'installed' && (
                server.golden_template_status === 'building' ? (
                    <div className="info-banner" style={{ background: 'rgba(251,191,36,0.1)', borderColor: 'rgba(251,191,36,0.3)', }}>
                        <Loader2 size={20} style={{ color: '#fbbf24' }} className="spinning" />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>Building golden template…</div>
                            <div style={{ fontSize: 12, opacity: 0.75 }}>Reopen to see live progress.</div>
                        </div>
                        <button className="btn btn-primary btn-sm" onClick={onCreateTemplate}><Wifi size={14} /> Show progress</button>
                    </div>
                ) : server.golden_template_vmid ? (
                    <div className="info-banner" style={{ background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.3)', }}>
                        <CheckCircle2 size={20} style={{ color: '#22c55e' }} />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>Golden template ready</div>
                            <div style={{ fontSize: 12, opacity: 0.75 }}>VMID {server.golden_template_vmid} — cloning bots here is ~3 seconds each.</div>
                        </div>
                        <button className="btn btn-secondary btn-sm" onClick={onCreateTemplate} title="Rebuild the golden template (e.g. after a bot code update)">
                            <RotateCw size={14} /> Rebuild
                        </button>
                    </div>
                ) : (
                    <div className="info-banner" style={{ }}>
                        <Download size={20} style={{ opacity: 0.7 }} />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>No golden template</div>
                            <div style={{ fontSize: 12, opacity: 0.75 }}>Clones provision from scratch (~3–5 min each). Create a template once to make cloning ~3 seconds.</div>
                        </div>
                        <button className="btn btn-primary btn-sm" onClick={onCreateTemplate}><Download size={14} /> Create Template</button>
                    </div>
                )
            )}

            {/* CPU/RAM/Disk */}
            <div className="info-stats-grid">
                <StatCard
                    icon={Cpu}
                    label="CPU"
                    pct={stats?.cpu_pct}
                    valueText={stats ? `${stats.cpu_pct}%` : '—'}
                    usageClass={stats ? usageClass(stats.cpu_pct) : 'normal'}
                    loading={statsLoading}
                    error={statsErr}
                />
                <StatCard
                    icon={MemoryStick}
                    label="Memory"
                    pct={stats?.mem_pct}
                    valueText={stats ? `${stats.mem_used_mb} / ${stats.mem_total_mb} MB` : '—'}
                    usageClass={stats ? usageClass(stats.mem_pct) : 'normal'}
                    loading={statsLoading}
                    error={statsErr}
                />
                <StatCard
                    icon={HardDrive}
                    label="Disk (/)"
                    pct={stats?.disk_pct}
                    valueText={stats ? `${stats.disk_used_gb} / ${stats.disk_total_gb} GB` : '—'}
                    usageClass={stats ? usageClass(stats.disk_pct) : 'normal'}
                    loading={statsLoading}
                    error={statsErr}
                />
            </div>

            {/* Server details */}
            <div className="info-details-card">
                <h4 style={{ margin: '0 0 12px 0' }}>Server details</h4>
                <div className="details-grid">
                    <Detail label="Name" value={server.name} />
                    <Detail label="Host IP" value={server.host_ip} mono />
                    <Detail label="Public IP" value={server.public_ip || server.host_ip} mono />
                    <Detail label="Bridge subnet" value={server.bridge_subnet || '10.10.10.0/24'} mono />
                    <Detail label="SSH user" value={server.ssh_user || 'root'} mono />
                    <Detail label="Asterisk LXC" value={server.asterisk_lxc_id || '—'} mono />
                    <Detail label="Status" value={server.asterisk_status || 'not_installed'} />
                    <Detail label="Uptime" value={stats?.uptime || '—'} />
                </div>
                {server.notes && (
                    <div style={{ marginTop: 12, fontSize: 13 }}>
                        <div className="detail-label">Notes</div>
                        <div className="detail-value" style={{ whiteSpace: 'pre-wrap' }}>{server.notes}</div>
                    </div>
                )}
            </div>
        </div>
    )
}

function StatCard({ icon: Icon, label, pct, error, loading, valueText, usageClass }) {
    return (
        <div className={`info-stat-card ${usageClass === 'critical' ? 'critical' : usageClass === 'warning' ? 'warning' : ''}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, opacity: 0.85 }}>
                <Icon size={16} />
                <span>{label}</span>
                {loading && <Loader2 size={12} className="spinning" style={{ marginLeft: 'auto', opacity: 0.5 }} />}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
                {error ? <span className="usage-na">—</span> : valueText}
            </div>
            {!error && pct != null && (
                <div className="usage-bar-container">
                    <div className={`usage-bar ${usageClass}`} style={{ width: `${Math.min(100, pct)}%` }}></div>
                </div>
            )}
            {error && <div style={{ fontSize: 11, opacity: 0.6 }}>{error.slice(0, 60)}</div>}
        </div>
    )
}

function Detail({ label, value, mono }) {
    return (
        <div className="detail-row">
            <span className="detail-label">{label}</span>
            <span className="detail-value" style={mono ? { fontFamily: 'monospace' } : {}}>{value}</span>
        </div>
    )
}

// ============================================================
// BOTS TAB
// ============================================================

function BotsTab({ server, machines, onReload, onCloneBots }) {
    const [expandedId, setExpandedId] = useState(null)
    const [statuses, setStatuses] = useState({})
    const [busy, setBusy] = useState({})
    const [envMachine, setEnvMachine] = useState(null)  // bot whose .env editor is open
    const [allLxcs, setAllLxcs] = useState(null)        // null = not loaded, [] = loaded empty
    const [lxcsLoading, setLxcsLoading] = useState(false)
    const [lxcsErr, setLxcsErr] = useState(null)
    const canClone = server.asterisk_status === 'installed'

    async function loadLxcs() {
        setLxcsLoading(true); setLxcsErr(null)
        try {
            const r = await audiosocketBotsApi.serverLxcs(server.id)
            if (r.data.success) setAllLxcs(r.data.lxcs || [])
            else { setAllLxcs([]); setLxcsErr(r.data.error || 'pct list failed') }
        } catch (e) {
            setAllLxcs([]); setLxcsErr(e.response?.data?.error || e.message)
        } finally { setLxcsLoading(false) }
    }
    useEffect(() => { loadLxcs() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [server.id])

    useEffect(() => {
        if (machines.length === 0) return
        let cancelled = false
        async function refreshAll() {
            const results = {}
            await Promise.all(machines.map(async m => {
                try {
                    const r = await audiosocketBotsApi.machineStatus(m.id)
                    results[m.id] = r.data
                } catch (e) {
                    results[m.id] = { error: e.message }
                }
            }))
            if (!cancelled) setStatuses(prev => ({ ...prev, ...results }))
        }
        refreshAll()
        const t = setInterval(refreshAll, 10000)
        return () => { cancelled = true; clearInterval(t) }
    }, [machines.map(m => m.id).join(',')])

    function lxcKind(lxc) {
        if (machines.find(m => parseInt(m.lxc_id) === lxc.id)) return 'bot'
        if (parseInt(server.asterisk_lxc_id) === lxc.id) return 'asterisk'
        return 'external'
    }

    async function doAction(m, action) {
        const verbMap = { start: 'starting', stop: 'stopping', restart: 'restarting' }
        setBusy(b => ({ ...b, [m.id]: verbMap[action] }))
        try {
            if (action === 'start')   await audiosocketBotsApi.startMachine(m.id)
            if (action === 'stop')    await audiosocketBotsApi.stopMachine(m.id)
            if (action === 'restart') await audiosocketBotsApi.restartMachine(m.id)
            const r = await audiosocketBotsApi.machineStatus(m.id)
            setStatuses(prev => ({ ...prev, [m.id]: r.data }))
        } catch (e) {
            alert(`${action} failed: ${e.response?.data?.error || e.message}`)
        } finally {
            setBusy(b => { const n = { ...b }; delete n[m.id]; return n })
        }
    }

    async function deleteMachine(m) {
        if (!window.confirm(`Delete bot ${m.sip_user} (LXC ${m.lxc_id})?\n\nThis stops the bot, destroys its LXC, removes DB rows, and re-syncs Asterisk.`)) return
        setBusy(b => ({ ...b, [m.id]: 'deleting' }))
        try {
            await audiosocketBotsApi.deleteMachine(m.id)
            await onReload()
        } catch (e) {
            alert(`Delete failed: ${e.response?.data?.error || e.message}`)
        } finally {
            setBusy(b => { const n = { ...b }; delete n[m.id]; return n })
        }
    }

    return (
        <div className="vms-tab">
            <div className="vm-toolbar">
                <div className="vm-toolbar-left">
                    <span style={{ fontSize: 13, opacity: 0.75 }}>
                        {machines.length} bot{machines.length === 1 ? '' : 's'} on this server
                    </span>
                </div>
                <div className="vm-toolbar-right">
                    <button className="btn btn-secondary btn-sm" onClick={onReload}>
                        <RefreshCw size={14} /> Refresh
                    </button>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={onCloneBots}
                        disabled={!canClone}
                        title={canClone ? 'Clone bots from an AudioSocket template onto this server' : 'Install Asterisk first'}>
                        <Plus size={14} /> Clone Bots
                    </button>
                </div>
            </div>

            <div className="tw-mx-5 tw-mb-2 tw-mt-4 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted">
                Registered bots
            </div>
            <div className="vm-table-container">
                {machines.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', opacity: 0.6 }}>
                        {canClone
                            ? 'No registered bots on this server yet. Click "Clone Bots" to add some.'
                            : 'Install Asterisk Stack first (Info tab), then clone bots here.'}
                    </div>
                ) : (
                    <table className="vm-table">
                        <thead>
                            <tr>
                                <th style={{ width: 28 }}></th>
                                <th>SIP</th>
                                <th>LXC</th>
                                <th>IP</th>
                                <th>Bot</th>
                                <th>STT</th>
                                <th>Last seen</th>
                                <th className="actions-cell">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {machines.map(m => {
                                const st = statuses[m.id]
                                const botActive = st?.bot?.active
                                const sttActive = st?.bot?.stt_active
                                const busyState = busy[m.id]
                                const expanded = expandedId === m.id
                                return (
                                    <React.Fragment key={m.id}>
                                        <tr>
                                            <td>
                                                <button
                                                    onClick={() => setExpandedId(expanded ? null : m.id)}
                                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                                                    {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                </button>
                                            </td>
                                            <td className="sip-cell"><strong>{m.sip_user}</strong></td>
                                            <td className="vmid">{m.lxc_id}</td>
                                            <td className="vmid">{m.lxc_ip}</td>
                                            <td>
                                                <span className={`status-badge-sm ${botActive ? 'online' : 'offline'}`}>
                                                    {botActive === undefined ? '—' : botActive ? 'online' : 'offline'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`status-badge-sm ${sttActive ? 'online' : 'offline'}`}>
                                                    {sttActive === undefined ? '—' : sttActive ? 'online' : 'offline'}
                                                </span>
                                            </td>
                                            <td className="time-cell">
                                                {m.last_seen_at ? new Date(m.last_seen_at).toLocaleString() : '—'}
                                            </td>
                                            <td className="actions-cell">
                                                {botActive
                                                    ? <ActionBtn icon={Square} label="Stop" busy={busyState === 'stopping'} onClick={() => doAction(m, 'stop')} />
                                                    : <ActionBtn icon={Play} label="Start" busy={busyState === 'starting'} onClick={() => doAction(m, 'start')} success />}
                                                <ActionBtn icon={RotateCw} label="Restart" busy={busyState === 'restarting'} onClick={() => doAction(m, 'restart')} />
                                                <ActionBtn icon={FileCog} label="Settings (.env)" onClick={() => setEnvMachine(m)} />
                                                <ActionBtn icon={Trash2} label="Delete" busy={busyState === 'deleting'} onClick={() => deleteMachine(m)} danger />
                                            </td>
                                        </tr>
                                        {expanded && (
                                            <tr>
                                                <td colSpan={8} style={{ padding: 0, background: 'rgba(0,0,0,0.2)' }}>
                                                    <MachineDetail machine={m} status={st} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ===== ALL LXCs ON THIS HOST ===== */}
            <div className="tw-mx-5 tw-mb-2 tw-mt-8 tw-flex tw-items-center tw-justify-between">
                <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted">
                    All LXCs on this host {allLxcs && `(${allLxcs.length})`}
                </div>
                <button className="btn btn-icon btn-sm" onClick={loadLxcs} title="Refresh">
                    {lxcsLoading ? <Loader2 size={14} className="spinning" /> : <RefreshCw size={14} />}
                </button>
            </div>
            <div className="vm-table-container">
                {lxcsErr ? (
                    <div style={{ padding: 16, fontSize: 12 }} className="test-result error">
                        Couldn't pct list on this host: {lxcsErr}
                    </div>
                ) : allLxcs === null || lxcsLoading ? (
                    <div style={{ padding: 16, opacity: 0.6, fontSize: 13 }}>loading…</div>
                ) : allLxcs.length === 0 ? (
                    <div style={{ padding: 16, opacity: 0.6, fontSize: 13 }}>No LXCs found on host.</div>
                ) : (
                    <table className="vm-table">
                        <thead>
                            <tr>
                                <th>LXC</th>
                                <th>Name</th>
                                <th>IP</th>
                                <th>Status</th>
                                <th>Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allLxcs.sort((a, b) => a.id - b.id).map(l => {
                                const kind = lxcKind(l)
                                const kindColor = kind === 'bot' ? '#22c55e' :
                                                  kind === 'asterisk' ? '#3b82f6' :
                                                  '#9ca3af'
                                const kindLabel = kind === 'bot' ? 'bot' :
                                                  kind === 'asterisk' ? 'asterisk' :
                                                  'external'
                                return (
                                    <tr key={l.id}>
                                        <td className="vmid">{l.id}</td>
                                        <td>{l.name || '—'}</td>
                                        <td className="vmid">{l.ip || '—'}</td>
                                        <td>
                                            <span className={`status-badge-sm ${l.status === 'running' ? 'online' : 'offline'}`}>
                                                {l.status}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{
                                                fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                                                padding: '2px 6px', borderRadius: 4,
                                                color: kindColor,
                                                border: `1px solid ${kindColor}40`,
                                                background: `${kindColor}15`
                                            }}>
                                                {kindLabel}
                                            </span>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>
            {envMachine && <BotEnvModal machine={envMachine} onClose={() => setEnvMachine(null)} />}
        </div>
    )
}

function ActionBtn({ icon: Icon, label, onClick, busy, success, danger }) {
    return (
        <button
            className={`action-btn ${success ? 'success' : ''} ${danger ? 'danger' : ''}`}
            onClick={onClick}
            disabled={busy}
            title={label}>
            {busy ? <Loader2 size={12} className="spinning" /> : <Icon size={12} />}
        </button>
    )
}

function MachineDetail({ machine, status }) {
    const [log, setLog] = useState('')
    const [logWhich, setLogWhich] = useState('bot')
    const [recordings, setRecordings] = useState([])
    const [logLoading, setLogLoading] = useState(false)
    const [recLoading, setRecLoading] = useState(false)
    const [transcript, setTranscript] = useState(null)

    async function loadLog() {
        setLogLoading(true)
        try {
            const r = await audiosocketBotsApi.machineLog(machine.id, { which: logWhich, lines: 200 })
            setLog(r.data.log || '')
        } catch (e) {
            setLog(`Failed: ${e.response?.data?.error || e.message}`)
        } finally { setLogLoading(false) }
    }
    async function loadRec() {
        setRecLoading(true)
        try {
            const r = await audiosocketBotsApi.machineRecordings(machine.id)
            setRecordings(r.data.recordings || [])
        } catch (e) { setRecordings([]) }
        finally { setRecLoading(false) }
    }
    async function showTranscript(rec) {
        const txt = rec.filename.replace(/\.wav$/, '.txt')
        try {
            const r = await audiosocketBotsApi.machineTranscript(machine.id, txt)
            setTranscript({ filename: txt, text: r.data.text || '(empty)' })
        } catch (e) {
            setTranscript({ filename: txt, text: `error: ${e.response?.data?.error || e.message}` })
        }
    }
    useEffect(() => { loadLog(); loadRec() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [machine.id, logWhich])
    useEffect(() => {
        const t = setInterval(loadLog, 5000)
        return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [machine.id, logWhich])

    return (
        <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <strong style={{ fontSize: 12, opacity: 0.8 }}>Live log ({logWhich})</strong>
                    <div style={{ display: 'flex', gap: 4 }}>
                        <button
                            onClick={() => setLogWhich('bot')}
                            className="btn btn-sm"
                            style={{ background: logWhich === 'bot' ? '#3b82f6' : 'transparent', color: logWhich === 'bot' ? '#fff' : 'inherit', minWidth: 'auto', padding: '2px 8px' }}>
                            bot
                        </button>
                        <button
                            onClick={() => setLogWhich('stt')}
                            className="btn btn-sm"
                            style={{ background: logWhich === 'stt' ? '#3b82f6' : 'transparent', color: logWhich === 'stt' ? '#fff' : 'inherit', minWidth: 'auto', padding: '2px 8px' }}>
                            stt
                        </button>
                        <button className="btn btn-icon btn-sm" onClick={loadLog}>
                            {logLoading ? <Loader2 size={12} className="spinning" /> : <RefreshCw size={12} />}
                        </button>
                    </div>
                </div>
                <pre style={{
                    background: '#000', color: '#86efac', padding: 8, fontSize: 11,
                    borderRadius: 4, height: 260, overflow: 'auto', margin: 0, fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap'
                }}>
                    {log || '(no log yet)'}
                </pre>
                {status?.bot && (
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
                        uptime: {status.bot.uptime} · listening: {status.bot.listening ? 'yes (:9999)' : 'no'}
                    </div>
                )}
            </div>
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <strong style={{ fontSize: 12, opacity: 0.8 }}>Recordings ({recordings.length})</strong>
                    <button className="btn btn-icon btn-sm" onClick={loadRec}>
                        {recLoading ? <Loader2 size={12} className="spinning" /> : <RefreshCw size={12} />}
                    </button>
                </div>
                <div style={{ background: '#000', borderRadius: 4, height: 260, overflow: 'auto', padding: 8 }}>
                    {recordings.length === 0 ? (
                        <div style={{ opacity: 0.5, fontSize: 12 }}>no recordings yet</div>
                    ) : recordings.map(r => (
                        <div key={r.filename} style={{ borderBottom: '1px solid #2a2e36', padding: '6px 0' }}>
                            <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.filename}</div>
                            <div style={{ fontSize: 10, opacity: 0.6 }}>
                                {r.modified ? new Date(r.modified).toLocaleString() : '—'} · {(r.size / 1024).toFixed(1)} KB
                                {r.has_transcript && <span style={{ color: '#22c55e', marginLeft: 6 }}> · transcript</span>}
                            </div>
                            <div style={{ marginTop: 4 }}>
                                <audio
                                    controls
                                    preload="none"
                                    src={audiosocketBotsApi.machineRecordingUrl(machine.id, r.filename)}
                                    style={{ height: 28, maxWidth: '100%' }} />
                                {r.has_transcript && (
                                    <button
                                        onClick={() => showTranscript(r)}
                                        className="btn btn-sm"
                                        style={{ marginLeft: 6, color: '#3b82f6', minWidth: 'auto', padding: '2px 6px' }}>
                                        transcript
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {transcript && (
                <div className="modal-overlay" onClick={() => setTranscript(null)}>
                    <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 style={{ fontFamily: 'monospace', fontSize: 14 }}>{transcript.filename}</h3>
                            <button className="modal-close" onClick={() => setTranscript(null)}><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <pre style={{
                                background: '#0d1117', padding: 10, borderRadius: 4, fontSize: 12,
                                maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap'
                            }}>
                                {transcript.text}
                            </pre>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

// ============================================================
// SERVER FORM
// ============================================================

function ServerForm({ initial, onCancel, onSaved }) {
    const isEdit = !!initial.id
    const [form, setForm] = useState({
        name: '',
        host_ip: '',
        public_ip: '',
        bridge_subnet: '10.10.10.0/24',
        ssh_port: 22,
        ssh_user: 'root',
        ssh_password: '',
        notes: '',
        ...initial
    })
    const [saving, setSaving] = useState(false)
    const [testing, setTesting] = useState(false)
    const [testResult, setTestResult] = useState(null)
    const [err, setErr] = useState(null)
    const patch = (k, v) => setForm(f => ({ ...f, [k]: v }))

    async function test() {
        setTesting(true); setTestResult(null)
        try {
            const r = await audiosocketBotsApi.testServerConnection({
                id: form.id,                       // edit: blank password → server uses the stored one
                host_ip: form.host_ip,
                ssh_port: form.ssh_port,
                ssh_user: form.ssh_user,
                ssh_password: form.ssh_password
            })
            setTestResult({ ok: true, output: r.data.output })
        } catch (e) {
            setTestResult({ ok: false, error: e.response?.data?.error || e.message })
        } finally { setTesting(false) }
    }

    async function save() {
        setErr(null); setSaving(true)
        try {
            if (isEdit) await audiosocketBotsApi.updateServer(form.id, form)
            else        await audiosocketBotsApi.createServer(form)
            onSaved()
        } catch (e) {
            setErr(e.response?.data?.error || e.message)
        } finally { setSaving(false) }
    }

    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{isEdit ? `Edit server #${form.id}` : 'Add server'}</h3>
                    <button className="modal-close" onClick={onCancel}><X size={18} /></button>
                </div>
                <div className="modal-body">
                    <div className="form-section">
                        <div className="form-group">
                            <label>Name *</label>
                            <input value={form.name} onChange={e => patch('name', e.target.value)} placeholder="hetzner-1" />
                        </div>
                        <div className="form-group">
                            <label>Proxmox host IP *</label>
                            <input value={form.host_ip} onChange={e => patch('host_ip', e.target.value)} placeholder="65.109.55.87" />
                        </div>
                        <div className="form-group">
                            <label>Public IP (for SIP externaddr — default = host IP)</label>
                            <input value={form.public_ip || ''} onChange={e => patch('public_ip', e.target.value)} placeholder="(optional)" />
                        </div>
                        <div className="form-group">
                            <label>LXC bridge subnet</label>
                            <input value={form.bridge_subnet || ''} onChange={e => patch('bridge_subnet', e.target.value)} />
                        </div>
                    </div>

                    <div className="form-section">
                        <h4 style={{ margin: '12px 0 6px 0', fontSize: 13, opacity: 0.8 }}>
                            SSH access (used for pct create / pct exec)
                        </h4>
                        <div className="tw-grid tw-grid-cols-[1fr_120px] tw-gap-3">
                            <div className="form-group">
                                <label>SSH user</label>
                                <input value={form.ssh_user || 'root'} onChange={e => patch('ssh_user', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label>SSH port</label>
                                <input type="number" min="1" max="65535" value={form.ssh_port ?? 22}
                                    onChange={e => patch('ssh_port', e.target.value)} />
                            </div>
                        </div>
                        <div className="tw-mb-2 tw-text-xs tw-text-muted">
                            22 normally. A host behind NAT reached through a reverse tunnel uses the tunnel's port on the relay (e.g. 2222).
                        </div>
                        <div className="form-group">
                            <label>{isEdit ? 'SSH password (leave blank to keep existing)' : 'SSH password *'}</label>
                            <input type="password" value={form.ssh_password || ''} onChange={e => patch('ssh_password', e.target.value)} />
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-group">
                            <label>Notes</label>
                            <textarea rows={3} value={form.notes || ''} onChange={e => patch('notes', e.target.value)} />
                        </div>
                    </div>

                    {testResult && (
                        <div className={`test-result ${testResult.ok ? 'success' : 'error'}`}>
                            {testResult.ok ? `OK: ${testResult.output}` : `FAILED: ${testResult.error}`}
                        </div>
                    )}
                    {err && <div className="test-result error">{err}</div>}
                </div>
                <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                    <button className="btn btn-secondary" onClick={test} disabled={testing || !form.host_ip || (!form.ssh_password && !isEdit)}>
                        {testing ? <><Loader2 size={14} className="spinning" /> Testing…</> : <><Wifi size={14} /> Test connection</>}
                    </button>
                    <button className="btn btn-primary" onClick={save} disabled={saving}>
                        {saving ? <><Loader2 size={14} className="spinning" /> Saving…</> : <><Save size={14} /> {isEdit ? 'Save' : 'Create'}</>}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ============================================================
// INSTALL ASTERISK MODAL
// ============================================================

function InstallAsteriskModal({ server, force, onClose }) {
    const [job, setJob] = useState(null)
    const [starting, setStarting] = useState(false)
    const [err, setErr] = useState(null)

    async function start() {
        setErr(null); setStarting(true)
        try {
            const r = await audiosocketBotsApi.installAsterisk(server.id, force ? { force: true } : {})
            const jobId = r.data.job_id
            if (!jobId) throw new Error('Backend did not return job_id')
            setJob({ id: jobId, status: 'running', progress: 0, step: 'queued', log_tail: '' })
        } catch (e) {
            setErr(e.response?.data?.error || e.message)
        } finally { setStarting(false) }
    }

    // On open: if server is already installing (and we're not forcing a reinstall),
    // find the in-flight job. Otherwise start a new one.
    useEffect(() => {
        if (server.asterisk_status === 'installing' && !force) {
            audiosocketBotsApi.listInstallJobs()
                .then(r => {
                    const jobs = r.data.jobs || []
                    const j = jobs.find(j => j.server_id === server.id && j.status === 'running')
                    if (j) setJob(j)
                    else start()
                })
                .catch(() => start())
        } else {
            start()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!job || job.status !== 'running') return
        let cancelled = false
        const tick = async () => {
            try {
                const r = await audiosocketBotsApi.getInstallJob(job.id)
                if (cancelled) return
                setJob(r.data.job)
                if (r.data.job.status === 'running') setTimeout(tick, 1500)
            } catch (_) {
                if (!cancelled) setTimeout(tick, 3000)
            }
        }
        const t = setTimeout(tick, 800)
        return () => { cancelled = true; clearTimeout(t) }
    }, [job?.id, job?.status])

    const isDone = job && (job.status === 'done' || job.status === 'failed')

    return (
        <div className="modal-overlay" onClick={isDone ? onClose : undefined}>
            <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>
                        {!job ? (force ? 'Reinstall Asterisk Stack' : 'Install Asterisk Stack') :
                         job.status === 'done'   ? (force ? 'Asterisk reinstall complete' : 'Asterisk install complete') :
                         job.status === 'failed' ? (force ? 'Asterisk reinstall failed' : 'Asterisk install failed') :
                                                   `${force ? 'Reinstalling' : 'Installing'} Asterisk on ${server.name}…`}
                    </h3>
                    <button className="modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="modal-body">
                    {err && <div className="test-result error">{err}</div>}

                    {!job && !err && (
                        <>
                            <p style={{ fontSize: 13, opacity: 0.8 }}>
                                This creates a fresh LXC on <strong>{server.host_ip}</strong> and installs Asterisk inside.
                                Takes 3–5 minutes the first time (Ubuntu template + Asterisk packages).
                            </p>
                            <button className="btn btn-primary" onClick={start} disabled={starting}>
                                {starting ? <><Loader2 size={14} className="spinning" /> Starting…</> : <><Download size={14} /> Start install</>}
                            </button>
                        </>
                    )}

                    {job && (
                        <>
                            <div className="bulk-clone-progress-container" style={{ marginBottom: 12 }}>
                                <div className="bulk-clone-progress-bar">
                                    <div className="bulk-clone-progress-fill"
                                        style={{
                                            width: `${job.progress || 0}%`,
                                            background: job.status === 'failed' ? '#dc2626' :
                                                        job.status === 'done'   ? '#22c55e' : '#3b82f6'
                                        }} />
                                </div>
                                <div style={{ marginTop: 6, fontSize: 12, fontFamily: 'monospace', opacity: 0.85 }}>
                                    {job.progress || 0}% — {job.step || ''}
                                </div>
                            </div>

                            {job.asterisk_lxc_id && (
                                <div style={{ fontSize: 13, marginBottom: 12 }}>
                                    Asterisk LXC: <strong>{job.asterisk_lxc_id}</strong>
                                    {job.asterisk_ip && <> at <code>{job.asterisk_ip}</code></>}
                                </div>
                            )}

                            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Live log</div>
                            <pre style={{
                                background: '#000', color: '#86efac', padding: 10, fontSize: 11,
                                borderRadius: 4, height: 240, overflow: 'auto', margin: 0, fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {job.log_tail || '(starting…)'}
                            </pre>

                            {job.error && <div className="test-result error" style={{ marginTop: 12 }}>{job.error}</div>}

                            <p style={{ fontSize: 11, opacity: 0.6, marginTop: 12 }}>
                                Install runs in the background. You can close this and reopen — the job keeps going.
                            </p>
                        </>
                    )}
                </div>
                <div className="modal-actions">
                    {isDone
                        ? <button className="btn btn-primary" onClick={onClose}>Close</button>
                        : <button className="btn btn-secondary" onClick={onClose}>Hide</button>}
                </div>
            </div>
        </div>
    )
}

// ============================================================
// CREATE GOLDEN TEMPLATE MODAL
// ============================================================

function CreateTemplateModal({ server, force, onClose }) {
    const [job, setJob] = useState(null)
    const [starting, setStarting] = useState(false)
    const [err, setErr] = useState(null)

    async function start() {
        setErr(null); setStarting(true)
        try {
            const r = await audiosocketBotsApi.createGoldenTemplate(server.id, force ? { force: true } : {})
            const jobId = r.data.job_id
            if (!jobId) throw new Error('Backend did not return job_id')
            setJob({ id: jobId, status: 'running', progress: 0, step: 'queued', log_tail: '' })
        } catch (e) {
            setErr(e.response?.data?.error || e.message)
        } finally { setStarting(false) }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { start() }, [])

    useEffect(() => {
        if (!job || job.status !== 'running') return
        let cancelled = false
        const tick = async () => {
            try {
                const r = await audiosocketBotsApi.getTemplateJob(job.id)
                if (cancelled) return
                setJob(r.data.job)
                if (r.data.job.status === 'running') setTimeout(tick, 2000)
            } catch (_) { if (!cancelled) setTimeout(tick, 3000) }
        }
        const t = setTimeout(tick, 800)
        return () => { cancelled = true; clearTimeout(t) }
    }, [job?.id, job?.status])

    const isDone = job && (job.status === 'done' || job.status === 'failed')

    return (
        <div className="modal-overlay" onClick={isDone ? onClose : undefined}>
            <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>
                        {!job ? 'Create golden bot template' :
                         job.status === 'done'   ? 'Golden template ready' :
                         job.status === 'failed' ? 'Template build failed' :
                                                   `Building golden template on ${server.name}…`}
                    </h3>
                    <button className="modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="modal-body">
                    {err && <div className="test-result error">{err}</div>}
                    {job && (
                        <>
                            <div className="bulk-clone-progress-container" style={{ marginBottom: 12 }}>
                                <div className="bulk-clone-progress-bar">
                                    <div className="bulk-clone-progress-fill"
                                        style={{ width: `${job.progress || 0}%`, background: job.status === 'failed' ? '#dc2626' : job.status === 'done' ? '#22c55e' : '#3b82f6' }} />
                                </div>
                                <div style={{ marginTop: 6, fontSize: 12, fontFamily: 'monospace', opacity: 0.85 }}>
                                    {job.progress || 0}% — {job.step || ''}
                                </div>
                            </div>
                            {job.golden_vmid && <div style={{ fontSize: 13, marginBottom: 12 }}>Golden template VMID: <strong>{job.golden_vmid}</strong></div>}
                            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Live log</div>
                            <pre style={{ background: '#000', color: '#86efac', padding: 10, fontSize: 11, borderRadius: 4, height: 240, overflow: 'auto', margin: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                {job.log_tail || '(provisioning — Moonshine model download takes a few minutes…)'}
                            </pre>
                            {job.error && <div className="test-result error" style={{ marginTop: 12 }}>{job.error}</div>}
                            <p style={{ fontSize: 11, opacity: 0.6, marginTop: 12 }}>
                                Runs in the background — you can close this and reopen. After it finishes, cloning bots on this server is ~3 seconds each.
                            </p>
                        </>
                    )}
                </div>
                <div className="modal-actions">
                    {isDone ? <button className="btn btn-primary" onClick={onClose}>Close</button> : <button className="btn btn-secondary" onClick={onClose}>Hide</button>}
                </div>
            </div>
        </div>
    )
}

// ============================================================
// CLONE BOTS MODAL
// ============================================================

function CloneBotsModal({ server, onClose }) {
    const [templates, setTemplates] = useState([])
    const [templateId, setTemplateId] = useState('')
    const [count, setCount] = useState(1)
    const [baseLxcId, setBaseLxcId] = useState(500)
    const [tplLoading, setTplLoading] = useState(true)
    const [running, setRunning] = useState(false)
    const [err, setErr] = useState(null)
    const [job, setJob] = useState(null)

    useEffect(() => {
        audiosocketBotsApi.listTemplates()
            .then(r => {
                const list = r.data.templates || []
                setTemplates(list)
                if (list.length > 0) setTemplateId(String(list[0].id))
            })
            .catch(() => {})
            .finally(() => setTplLoading(false))
    }, [])

    async function run() {
        if (!templateId) { setErr('Pick a template'); return }
        setErr(null); setRunning(true)
        try {
            const r = await audiosocketBotsApi.clone(parseInt(templateId), {
                count, server_id: server.id, base_lxc_id: baseLxcId
            })
            setJob({ id: r.data.job_id, progress: 0, step: 'starting…', status: 'running', results: [] })
        } catch (e) {
            setErr(e.response?.data?.error || e.message)
            setRunning(false)
        }
    }

    useEffect(() => {
        if (!job || job.status !== 'running') return
        let cancelled = false
        const tick = async () => {
            try {
                const r = await audiosocketBotsApi.getCloneJob(job.id)
                if (cancelled) return
                setJob(r.data.job)
                if (r.data.job.status === 'running') setTimeout(tick, 1500)
            } catch (_) {
                if (!cancelled) setTimeout(tick, 3000)
            }
        }
        const t = setTimeout(tick, 800)
        return () => { cancelled = true; clearTimeout(t) }
    }, [job?.id, job?.status])

    const isDone = job && job.status !== 'running'

    return (
        <div className="modal-overlay" onClick={isDone ? onClose : undefined}>
            <div className="modal" style={{ maxWidth: 650 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>
                        {!job ? `Clone bots onto ${server.name}` :
                         job.status === 'done'   ? 'Clone complete' :
                         job.status === 'failed' ? 'Clone failed' :
                                                   'Cloning bots…'}
                    </h3>
                    <button className="modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="modal-body">
                    {!job && (
                        <>
                            <div className="form-group">
                                <label>Client template (SIP range / Vicidial / avatar)</label>
                                {tplLoading
                                    ? <div style={{ fontSize: 12, opacity: 0.6 }}>loading…</div>
                                    : templates.length === 0
                                    ? <div className="test-result error">No templates available. Create one on /templates first.</div>
                                    : (
                                        <select value={templateId} onChange={e => setTemplateId(e.target.value)}>
                                            {templates.map(t => (
                                                <option key={t.id} value={t.id}>
                                                    {t.name} — SIPs {t.sip_user_start}–{t.sip_user_end} → {t.vicidial_ip}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                            </div>
                            <div className="form-group">
                                <label>Clone source (image)</label>
                                {server.golden_template_vmid && server.golden_template_status === 'ready' ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 6, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', fontSize: 13 }}>
                                        <Zap size={14} style={{ color: '#22c55e' }} />
                                        <span>Golden template <strong>VMID {server.golden_template_vmid}</strong> — fast clone (~3–15s/bot)</span>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 6, background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.3)', fontSize: 13 }}>
                                        <Download size={14} style={{ opacity: 0.7 }} />
                                        <span>Provision from scratch (~3–5 min/bot) — no golden template on this server</span>
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Number of bots to create</label>
                                <input type="number" min={1} max={20} value={count}
                                    onChange={e => setCount(parseInt(e.target.value) || 1)} />
                            </div>
                            <div className="form-group">
                                <label>Base LXC id (search for first free above this)</label>
                                <input type="number" value={baseLxcId}
                                    onChange={e => setBaseLxcId(parseInt(e.target.value) || 500)} />
                            </div>
                            <p style={{ fontSize: 12, opacity: 0.6 }}>
                                {server.golden_template_vmid && server.golden_template_status === 'ready'
                                    ? `⚡ This server has a golden template (VMID ${server.golden_template_vmid}) — each bot clones in ~3–15 seconds.`
                                    : 'No golden template on this server — each LXC takes ~3–5 min (Moonshine model download). Tip: build a template on the Info tab to make this instant.'}
                            </p>
                            {err && <div className="test-result error">{err}</div>}
                        </>
                    )}

                    {job && (
                        <>
                            <div className="bulk-clone-progress-container">
                                <div className="bulk-clone-progress-bar">
                                    <div className="bulk-clone-progress-fill"
                                        style={{
                                            width: `${job.progress || 0}%`,
                                            background: job.status === 'failed' ? '#dc2626' :
                                                        job.status === 'done'   ? '#22c55e' : '#3b82f6'
                                        }} />
                                </div>
                                <div style={{ marginTop: 6, fontSize: 12, fontFamily: 'monospace', opacity: 0.85 }}>
                                    {job.progress || 0}% — {job.step || ''}
                                </div>
                            </div>

                            {job.results && job.results.length > 0 && (
                                <table className="vm-table" style={{ marginTop: 14, fontSize: 12 }}>
                                    <thead>
                                        <tr>
                                            <th>SIP</th><th>LXC</th><th>IP</th><th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {job.results.map(r => {
                                            const color = r.status === 'provisioned' ? '#22c55e' :
                                                          (r.status === 'failed' || r.status === 'provision_failed') ? '#fca5a5' :
                                                          r.status === 'queued' ? '#9ca3af' : '#fbbf24'
                                            return (
                                                <tr key={r.sip_user}>
                                                    <td className="sip-cell">{r.sip_user}</td>
                                                    <td className="vmid">{r.lxc_id || '—'}</td>
                                                    <td className="vmid">{r.lxc_ip || '—'}</td>
                                                    <td style={{ color }}>{r.status}{r.error ? ` — ${r.error.slice(0, 60)}` : ''}</td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            )}

                            {job.error && <div className="test-result error" style={{ marginTop: 12 }}>{job.error}</div>}

                            <p style={{ fontSize: 11, opacity: 0.6, marginTop: 12 }}>
                                Clone runs in the background. You can close this and reopen — the job keeps going.
                            </p>
                        </>
                    )}
                </div>
                <div className="modal-actions">
                    {!job && (
                        <>
                            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                            <button
                                className="btn btn-primary"
                                onClick={run}
                                disabled={running || !templateId || templates.length === 0}>
                                {running ? <><Loader2 size={14} className="spinning" /> Starting…</> : <><Plus size={14} /> Clone</>}
                            </button>
                        </>
                    )}
                    {job && !isDone && <button className="btn btn-secondary" onClick={onClose}>Hide</button>}
                    {isDone           && <button className="btn btn-primary"   onClick={onClose}>Close</button>}
                </div>
            </div>
        </div>
    )
}
